import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import tasksService, { Task, TaskFilters, TasksResponse } from '../../services/api/tasksService';

interface TasksState {
  tasks: Task[];
  currentTask: Task | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  filters: TaskFilters;
  loading: boolean;
  error: string | null;
}

const initialState: TasksState = {
  tasks: [],
  currentTask: null,
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  filters: {},
  loading: false,
  error: null,
};

// Async thunks
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (filters: TaskFilters | undefined, { rejectWithValue }) => {
    try {
      const response = await tasksService.getTasks(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch tasks');
    }
  }
);

export const fetchTaskById = createAsyncThunk(
  'tasks/fetchTaskById',
  async (id: string, { rejectWithValue }) => {
    try {
      const task = await tasksService.getTaskById(id);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch task');
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (taskData: Partial<Task>, { rejectWithValue }) => {
    try {
      const task = await tasksService.createTask(taskData);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create task');
    }
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ id, taskData }: { id: string; taskData: Partial<Task> }, { rejectWithValue }) => {
    try {
      const task = await tasksService.updateTask(id, taskData);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update task');
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (id: string, { rejectWithValue }) => {
    try {
      await tasksService.deleteTask(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete task');
    }
  }
);

export const updateTaskStatus = createAsyncThunk(
  'tasks/updateTaskStatus',
  async ({ id, status }: { id: string; status: string }, { rejectWithValue }) => {
    try {
      const task = await tasksService.updateTaskStatus(id, status);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update task status');
    }
  }
);

export const updateTaskProgress = createAsyncThunk(
  'tasks/updateTaskProgress',
  async ({ id, percentComplete }: { id: string; percentComplete: number }, { rejectWithValue }) => {
    try {
      const task = await tasksService.updateTaskProgress(id, percentComplete);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update task progress');
    }
  }
);

export const overrideTaskStatus = createAsyncThunk(
  'tasks/overrideTaskStatus',
  async (
    {
      id,
      payload,
    }: {
      id: string;
      payload: Parameters<typeof tasksService.overrideTaskStatus>[1];
    },
    { rejectWithValue }
  ) => {
    try {
      const task = await tasksService.overrideTaskStatus(id, payload);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to override task status');
    }
  }
);

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<TaskFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearCurrentTask: (state) => {
      state.currentTask = null;
    },
    setTasksFromCache: (state, action: PayloadAction<Task[]>) => {
      state.tasks = action.payload;
      state.total = action.payload.length;
      state.page = 1;
      state.limit = action.payload.length;
      state.totalPages = 1;
      state.loading = false;
      state.error = null;
    },
    setCurrentTaskFromCache: (state, action: PayloadAction<Task>) => {
      state.currentTask = action.payload;
      const index = state.tasks.findIndex((task) => task.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      state.loading = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch tasks
    builder.addCase(fetchTasks.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTasks.fulfilled, (state, action: PayloadAction<TasksResponse>) => {
      state.loading = false;
      state.tasks = action.payload.tasks;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.totalPages = action.payload.totalPages;
    });
    builder.addCase(fetchTasks.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch task by ID
    builder.addCase(fetchTaskById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTaskById.fulfilled, (state, action: PayloadAction<Task>) => {
      state.loading = false;
      state.currentTask = action.payload;
    });
    builder.addCase(fetchTaskById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create task
    builder.addCase(createTask.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createTask.fulfilled, (state, action: PayloadAction<Task>) => {
      state.loading = false;
      state.tasks.unshift(action.payload);
      state.total += 1;
    });
    builder.addCase(createTask.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update task
    builder.addCase(updateTask.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateTask.fulfilled, (state, action: PayloadAction<Task>) => {
      state.loading = false;
      const index = state.tasks.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      if (state.currentTask?.id === action.payload.id) {
        state.currentTask = action.payload;
      }
    });
    builder.addCase(updateTask.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete task
    builder.addCase(deleteTask.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteTask.fulfilled, (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.tasks = state.tasks.filter((t) => t.id !== action.payload);
      state.total -= 1;
    });
    builder.addCase(deleteTask.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update task status
    builder.addCase(updateTaskStatus.fulfilled, (state, action: PayloadAction<Task>) => {
      const index = state.tasks.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      if (state.currentTask?.id === action.payload.id) {
        state.currentTask = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateTaskStatus.rejected, (state, action) => {
      state.error = (action.payload as string) || 'Failed to update task status';
    });

    // Update task progress
    builder.addCase(updateTaskProgress.fulfilled, (state, action: PayloadAction<Task>) => {
      const index = state.tasks.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      if (state.currentTask?.id === action.payload.id) {
        state.currentTask = action.payload;
      }
    });

    builder.addCase(overrideTaskStatus.fulfilled, (state, action: PayloadAction<Task>) => {
      const index = state.tasks.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      if (state.currentTask?.id === action.payload.id) {
        state.currentTask = action.payload;
      }
    });
  },
});

export const { setFilters, clearFilters, clearCurrentTask, setTasksFromCache, setCurrentTaskFromCache, clearError } = tasksSlice.actions;
export default tasksSlice.reducer;
